import React, { useState } from 'react';
import { motion } from 'motion/react';
import { Stethoscope, Phone, Mail, MapPin, Building2, User, CheckCircle, BadgeCheck, Send } from 'lucide-react';
import { toast } from 'sonner';

const Care365Page = () => {
  const [form, setForm] = useState({
    doctor_name: '',
    title: '',
    hospital: '',
    phone: '',
    email: '',
    city: '',
    location: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.doctor_name || !form.title || !form.hospital || !form.phone || !form.city) {
      toast.error('Please fill in all required fields');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        email: form.email || null,
        location: form.location || null,
      };
      const res = await fetch(`${API_URL}/api/leads/care365`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setSubmitted(true);
        toast.success('Registration submitted successfully!');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Submission failed');
      }
    } catch {
      toast.error('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="care365-page" className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #022c22 0%, #064e3b 50%, #022c22 100%)' }}>
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="text-center p-10 rounded-2xl max-w-md mx-4"
          style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', backdropFilter: 'blur(16px)' }}
        >
          <div className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}>
            <CheckCircle size={40} className="text-white" />
          </div>
          <h2 data-testid="care365-success-title" className="text-2xl font-bold text-white mb-3">Thank you for your interest!</h2>
          <p className="text-gray-300 mb-6">Your profile has been submitted. Our qualified medical team will review and verify your credentials, and get in touch with you shortly to complete the onboarding.</p>
          <div className="text-sm text-gray-500">Reference ID saved. You can close this page.</div>
        </motion.div>
      </div>
    );
  }

  return (
    <div data-testid="care365-page" className="min-h-screen" style={{ background: 'linear-gradient(135deg, #022c22 0%, #064e3b 50%, #022c22 100%)' }}>
      {/* Hero */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 30% 40%, #10b981 0%, transparent 50%), radial-gradient(circle at 70% 60%, #06b6d4 0%, transparent 50%)' }} />
        <div className="max-w-4xl mx-auto px-4 pt-16 pb-10 relative z-10">
          <motion.div initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.5 }} className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6" style={{ background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.3)' }}>
              <Stethoscope size={18} className="text-emerald-400" />
              <span className="text-emerald-300 text-sm font-medium">Doctor Registration</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
              Join <span style={{ color: '#10b981' }}>Care365</span>
            </h1>
            <p className="text-lg text-gray-400 max-w-xl mx-auto mb-6">
              Register your interest as a healthcare provider. Connect with patients, grow your practice.
            </p>
            <div className="inline-flex items-center gap-2.5 px-5 py-3 rounded-xl" style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)' }}>
              <BadgeCheck size={20} className="text-emerald-400" />
              <span className="text-gray-300 text-sm font-medium">All submissions are reviewed and verified by our qualified medical team</span>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-lg mx-auto px-4 pb-20">
        <motion.form
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          onSubmit={handleSubmit}
          data-testid="care365-form"
          className="rounded-2xl p-8 space-y-5"
          style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(16px)' }}
        >
          {/* Doctor Name */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              <User size={14} className="inline mr-1.5 -mt-0.5" />Full Name *
            </label>
            <input
              data-testid="care365-doctor-name"
              type="text"
              name="doctor_name"
              value={form.doctor_name}
              onChange={handleChange}
              placeholder="Dr. Full Name"
              required
              className="w-full px-4 py-3 rounded-xl text-white placeholder-gray-500 outline-none transition-all focus:ring-2 focus:ring-emerald-500/50"
              style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
            />
          </div>

          {/* Title / Specialty */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              <BadgeCheck size={14} className="inline mr-1.5 -mt-0.5" />Title / Specialty *
            </label>
            <input
              data-testid="care365-title"
              type="text"
              name="title"
              value={form.title}
              onChange={handleChange}
              placeholder="e.g. General Practitioner, Cardiologist"
              required
              className="w-full px-4 py-3 rounded-xl text-white placeholder-gray-500 outline-none transition-all focus:ring-2 focus:ring-emerald-500/50"
              style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
            />
          </div>

          {/* Hospital */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              <Building2 size={14} className="inline mr-1.5 -mt-0.5" />Hospital / Clinic *
            </label>
            <input
              data-testid="care365-hospital"
              type="text"
              name="hospital"
              value={form.hospital}
              onChange={handleChange}
              placeholder="Hospital or clinic name"
              required
              className="w-full px-4 py-3 rounded-xl text-white placeholder-gray-500 outline-none transition-all focus:ring-2 focus:ring-emerald-500/50"
              style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
            />
          </div>

          {/* Phone / WhatsApp */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              <Phone size={14} className="inline mr-1.5 -mt-0.5" />Phone / WhatsApp *
            </label>
            <input
              data-testid="care365-phone"
              type="tel"
              name="phone"
              value={form.phone}
              onChange={handleChange}
              placeholder="+254 7XX XXX XXX"
              required
              className="w-full px-4 py-3 rounded-xl text-white placeholder-gray-500 outline-none transition-all focus:ring-2 focus:ring-emerald-500/50"
              style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              <Mail size={14} className="inline mr-1.5 -mt-0.5" />Email (optional)
            </label>
            <input
              data-testid="care365-email"
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              placeholder="doctor@example.com"
              className="w-full px-4 py-3 rounded-xl text-white placeholder-gray-500 outline-none transition-all focus:ring-2 focus:ring-emerald-500/50"
              style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
            />
          </div>

          {/* City */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              <MapPin size={14} className="inline mr-1.5 -mt-0.5" />City *
            </label>
            <input
              data-testid="care365-city"
              type="text"
              name="city"
              value={form.city}
              onChange={handleChange}
              placeholder="e.g. Nairobi, Dar es Salaam"
              required
              className="w-full px-4 py-3 rounded-xl text-white placeholder-gray-500 outline-none transition-all focus:ring-2 focus:ring-emerald-500/50"
              style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
            />
          </div>

          {/* Location */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              <MapPin size={14} className="inline mr-1.5 -mt-0.5" />Location / Address (optional)
            </label>
            <input
              data-testid="care365-location"
              type="text"
              name="location"
              value={form.location}
              onChange={handleChange}
              placeholder="Specific area or address"
              className="w-full px-4 py-3 rounded-xl text-white placeholder-gray-500 outline-none transition-all focus:ring-2 focus:ring-emerald-500/50"
              style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
            />
          </div>

          {/* Submit */}
          <button
            data-testid="care365-submit-btn"
            type="submit"
            disabled={loading}
            className="w-full py-4 rounded-xl font-semibold text-white flex items-center justify-center gap-2 transition-all hover:brightness-110 disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <>
                <Send size={18} /> Register as Provider
              </>
            )}
          </button>

          <p className="text-center text-xs text-gray-500">
            This is an expression of interest. Our medical team will verify your credentials before onboarding.
          </p>
        </motion.form>
      </div>
    </div>
  );
};

export default Care365Page;
